import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { TourLink } from './TourLink';
import { GuidedTour, buildMandateReaderSteps } from './WelcomeTour';
const MANDATE_TOUR_KEY = 'aml_tour_mandate_dismissed';
import { ActColumn } from './ActColumn';
import { PrintLink } from './PrintLink';
import { articleHashFor, mandateHashFor } from '@/hooks/useHashRoute';
import type { FilterState } from '@/types';
import styles from './MandateReader2Col.module.css';

const ACT_COLORS: Record<string, string> = { amld: '#1D4ED8', amlr: '#0D9488', amlar: '#7C3AED', tfr: '#C2410C' };

const NEUTRAL_FILTERS: FilterState = { query: '', types: [], statuses: [], hasMandates: false } as unknown as FilterState;

type CpRow = { cells: (string | string[])[] };
type CpBand = { section: string };
type CpTable = { headers: string[]; rows: (CpRow | CpBand)[] };

type CpSection = {
  num: string; title: string; text?: string; placeholder?: string; children?: CpSection[];
  table?: CpTable;
};

// --- structured rendering of verbatim CP text ---------------------------------
// Source text is hard-wrapped (newline every ~width chars) with NO blank lines, so
// paragraphs must be reflowed: join lines, breaking on bullets/sub-headings and after
// "short" lines (a line that didn't reach the wrap width signals a paragraph/item end).
// The wrap width is estimated from the 85th percentile of line lengths (robust to
// outlier long lines). Words are never altered — only grouping.
const BULLET = /^[•▪◦‣]\s*|^[–-]\s+/;
const HEADING = /^([A-H]\.\s+[A-Z]|Introduction\b|Approach\b|Baseline scenario\b|Problem identification\b|Policy objectives\b|Options considered\b|Preferred option\b|Conclusion\b)/;

function pctl(arr: number[], p: number): number {
  if (!arr.length) return 99;
  const a = [...arr].sort((x, y) => x - y);
  const k = (a.length - 1) * p, f = Math.floor(k);
  return f + 1 >= a.length ? a[f] : a[f] + (a[f + 1] - a[f]) * (k - f);
}

type Block = { t: 'p' | 'h' | 'li'; v: string };
function classifyBlock(b: string): Block {
  if (BULLET.test(b)) return { t: 'li', v: b.replace(BULLET, '').trim() };
  if (HEADING.test(b) && b.length < 90) return { t: 'h', v: b };
  return { t: 'p', v: b };
}
function parseBlocks(text: string): Block[] {
  const lines = text.split('\n').map(l => l.replace(/\s+$/, ''));
  const lens = lines.filter(l => l.trim() && !BULLET.test(l.trim())).map(l => l.length);
  const thresh = pctl(lens, 0.85) * 0.82;
  const out: Block[] = [];
  let cur = '';
  const flush = () => { if (cur.trim()) out.push(classifyBlock(cur.trim())); cur = ''; };
  for (const raw of lines) {
    const L = raw.trim();
    if (!L) { flush(); continue; }
    if (BULLET.test(L) || HEADING.test(L)) { flush(); cur = L; }
    else cur += (cur ? ' ' : '') + L;
    if (raw.length < thresh) flush();
  }
  flush();
  return out;
}

function StructuredText({ text }: { text: string }) {
  const blocks = useMemo(() => parseBlocks(text), [text]);
  const out: ReactElement[] = [];
  let li: string[] = [];
  const flushLi = (key: string) => {
    if (li.length) { out.push(<ul key={key}>{li.map((x, i) => <li key={i}>{x}</li>)}</ul>); li = []; }
  };
  blocks.forEach((b, i) => {
    if (b.t === 'li') { li.push(b.v); return; }
    flushLi('u' + i);
    out.push(b.t === 'h' ? <h4 key={i}>{b.v}</h4> : <p key={i}>{b.v}</p>);
  });
  flushLi('u-end');
  return <>{out}</>;
}

function DataTable({ table }: { table: CpTable }) {
  const ncol = table.headers.length;
  return (
    <table className={styles.dataTable}>
      <thead><tr>{table.headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
      <tbody>
        {table.rows.map((r, ri) => 'section' in r
          ? <tr className={styles.bandRow} key={ri}><td colSpan={ncol}>{r.section}</td></tr>
          : <tr key={ri}>{r.cells.map((c, ci) => (
              <td key={ci}>{(Array.isArray(c) ? c : [c]).map((p, pi) => <p key={pi}>{p}</p>)}</td>
            ))}</tr>
        )}
      </tbody>
    </table>
  );
}

// Recursive: a section may carry body text/table, point to a right column
// (placeholder) at any depth, and/or nest child sections to arbitrary depth.
function SectionNode({ s, color, level = 0 }: { s: CpSection; color: string; level?: number }) {
  // A placeholder section points to the right column instead of carrying body text.
  if (s.placeholder) {
    return (
      <div className={styles.placeholderRow} style={{ borderColor: color }}>
        <span className={styles.cNum} style={{ color }}>{s.num}{level === 0 ? '.' : ''}</span>
        <span className={styles.cTitle}>{s.title}</span>
        <div className={styles.seeRight} style={{ color }}>→ {s.placeholder.includes(' on the right')
          ? <>{s.placeholder.split(' on the right')[0]}<span className={styles.onlyWide}> on the right</span><span className={styles.onlyNarrow}> below</span>{s.placeholder.split(' on the right')[1]}</>
          : s.placeholder}</div>
      </div>
    );
  }
  const hasBody = !!(s.text && s.text.trim()) || !!s.table || !!(s.children && s.children.length > 0);
  return (
    <details className={level === 0 ? styles.top : styles.sub}>
      <summary>
        <span className={styles.cNum} style={{ color }}>{s.num}{level === 0 ? '.' : ''}</span>{level === 0 ? '' : ' '}
        <span className={styles.cTitle}>{s.title}</span>
      </summary>
      <div className={styles.cBody}>
        {s.text && s.text.trim() && <StructuredText text={s.text} />}
        {s.table && <DataTable table={s.table} />}
        {(s.children || []).map(c => <SectionNode key={c.num} s={c} color={color} level={level + 1} />)}
        {!hasBody && <p className={styles.quiet}>(see official document)</p>}
      </div>
    </details>
  );
}

export function MandateReader2Col({ inst, initialArt }: { inst: any; initialArt?: string }) {
  const color = ACT_COLORS[inst.actId] || '#0D9488';
  const contentsRef = useRef<HTMLDivElement>(null);
  const setAll = (open: boolean) => {
    contentsRef.current?.querySelectorAll('details').forEach(d => { (d as HTMLDetailsElement).open = open; });
  };

  useEffect(() => {
    document.title = `${inst.type} ${inst.legalBasis} — ${inst.title}`;
  }, [inst]);

  // Adapter: present the mandate (or a single guideline column) as an act-shaped
  // object so the same ActColumn that renders the four main acts can render it
  // (recitals · articles · annexes) with all its features.
  const makeActLike = (articles: any[], opts?: { shortName?: string; fullName?: string }) => ({
    id: inst.actId,
    shortName: opts?.shortName || `${inst.type} · ${inst.legalBasis}`,
    fullName: opts?.fullName || inst.title,
    cssClass: inst.actId,
    celex: inst.statusLabel || '',
    year: 0, num: 0, type: 'reg' as const,
    chapters: [{ title: '', articles: articles || [] }],
    recitals: inst.recitals || [],
    recitalGroups: inst.recitalGroups || [],
    // The data's "annexes" field mixes (a) consultation-paper §5 content (left column,
    // carried by cpSections) and (b) genuine annexes ("ANNEX I — …"). Keep only the latter.
    annexes: (inst.annexes || []).filter((a: { title?: string; heading?: string }) =>
      /^(ANNEX |Annex —)/.test(a.title || a.heading || '')
    ),
  });

  // Some mandates (e.g. gl_tfr_23) carry two guidelines, each rendered as its own
  // right column. When present, guidelineColumns drives a two-column right side.
  const guidelineColumns = inst.guidelineColumns as undefined |
    { title: string; subtitle?: string; source?: string; articles: any[] }[];

  // Single-column actLike (null when guidelineColumns is used).
  const actLike = useMemo(() => {
    if (guidelineColumns) return null;
    const base = makeActLike(inst.articles || []);
    if (inst.articleChapters && inst.articleChapters.length) {
      base.chapters = inst.articleChapters.map((ch: { num: string; title: string; articleNums?: (number | string)[] }) => ({
        title: `${ch.num} ${ch.title}`,
        // Explicit membership (RTS/ITS "SECTION N" headings spanning article ranges) or,
        // by default, grouping by num prefix (guideline sections "1", "1.x").
        articles: (inst.articles || []).filter((a: { num: string | number }) => {
          const n = String(a.num);
          if (ch.articleNums) return ch.articleNums.map(String).includes(n);
          return n === ch.num || n.startsWith(ch.num + '.');
        }),
      }));
    }
    return base;
  }, [inst]);

  // Cross-reference badges open the referenced base-act article in the tool's own
  // article reader (not EUR-Lex).
  const onCrossRefClick = (actId: string, num: number | string) => {
    window.open(articleHashFor(actId, num), '_blank', 'noopener');
  };

  // Reader tour: first visit (own dismissal key), re-openable from the Tour link.
  const [showTour, setShowTour] = useState(false); // secondary tours never auto-open — only via the Tour link
  const isFR = inst.sourcing?.amlaDocument?.docType === 'final_report';
  const docKind = isFR ? 'Final report' : 'Consultation paper';
  const draftTitle = inst.type === 'ITS' ? 'Draft implementing technical standards' : inst.type === 'GL' ? 'Draft guidelines' : 'Draft regulatory technical standards';
  const amlaUrl = (inst.sourcing?.amlaDocument?.url) || inst.documentUrl
    || 'https://www.amla.europa.eu/policy/regulatory-instruments_en';

  return (
    <div className={guidelineColumns ? `${styles.page} ${styles.page3}` : styles.page}>
      <GuidedTour open={showTour} onClose={() => setShowTour(false)} steps={buildMandateReaderSteps()} storageKey={MANDATE_TOUR_KEY} />
      {/* LEFT: consultation-paper wrapper */}
      <aside className={styles.left}>
        <div className={styles.leftStickyHead}>
          <div className={styles.kicker} data-tour="mr-kicker">
            <span>{inst.type} · {inst.legalBasis}</span>
            <span className={styles.statusPill}>{inst.statusLabel}</span>
            <div className={styles.headerControls} style={{ ['--act-color' as any]: color }}>
              <button type="button" className={styles.colCtrlBtn} title="Expand all" onClick={() => setAll(true)}>⊞</button>
              <button type="button" className={styles.colCtrlBtn} title="Collapse all" onClick={() => setAll(false)}>⊟</button>
            </div>
          </div>
          <div className={styles.mTitle} style={{ borderBottomColor: color }}>{inst.cpTitle || inst.title}</div>
          <button type="button" className={styles.jumpDraft} style={{ color, borderColor: color }}
            onClick={() => document.querySelector('[data-tour="mr-right-head"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            Go to the draft articles <span className={styles.onlyWide}>→</span><span className={styles.onlyNarrow}>↓</span>
          </button>
        </div>

        <div className={styles.links} data-tour="mr-links">
          <a className={styles.lnk} style={{ borderColor: color, color }} href={amlaUrl} target="_blank" rel="noopener">Official source — AMLA document ↗</a>
          <span className={`${styles.lnk} ${styles.disabled}`} title="Available once adopted">EUR-Lex EN/IT (once adopted)</span>
          <TourLink accent={color} onClick={() => setShowTour(true)} />
          <PrintLink accent={color} />
        </div>

        <div className={styles.summaryBox} style={{ borderLeftColor: color }} data-tour="mr-summary">
          <span className={styles.summaryLabel}>Mandate Summary</span>
          <p className={styles.summaryText}>{inst.mandateSummary || inst.title}</p>
          {inst.mandateSummary_disclaimer && <div className={styles.summaryDisclaimer}>{inst.mandateSummary_disclaimer}</div>}
        </div>

        <div className={styles.contentsLabel}>Contents</div>
        <div ref={contentsRef} data-tour="mr-contents">
          {(inst.cpSections as CpSection[]).map(s => <SectionNode key={s.num} s={s} color={color} />)}
        </div>

        <div className={styles.leftFoot}>{docKind} — verbatim sections. Always verify against the official AMLA document.</div>
      </aside>

      {/* RIGHT: the draft instrument(s), rendered with the same column as the main acts.
          When guidelineColumns is set, render one ActColumn per guideline side by side. */}
      {guidelineColumns ? (
        guidelineColumns.map((gc, i) => (
          <section className={styles.right} key={i}>
            <div className={styles.rightHead} style={{ borderColor: color }}>
              <div className={styles.rightHeadMain}>
                <span className={styles.rightHeadT} style={{ color }}>{gc.title}</span>
                {gc.subtitle && <span className={styles.rightHeadS}>{gc.subtitle}</span>}
              </div>
              {gc.source && <a className={styles.lnk} style={{ borderColor: color, color }} href={gc.source} target="_blank" rel="noopener">EBA ↗</a>}
            </div>
            <ActColumn
              act={makeActLike(gc.articles, { shortName: gc.title, fullName: gc.subtitle || gc.title }) as any}
              filters={NEUTRAL_FILTERS}
              onCrossRefClick={onCrossRefClick}
              hideSourceLink
              articleHideSources
              articleReaderHref={(num) => mandateHashFor(inst.id, `${i}~${num}`)}
              crossRefTarget={null}
            />
          </section>
        ))
      ) : (
        <section className={styles.right}>
          <div className={styles.rightHead} style={{ borderColor: color }} data-tour="mr-right-head">
            <div className={styles.rightHeadMain}>
              <span className={styles.rightHeadT} style={{ color }}>{draftTitle}</span>
              <span className={styles.rightHeadS}>{isFR ? 'final draft as submitted to the Commission' : 'draft as published for consultation'} · recitals · articles · annexes</span>
            </div>
            <PrintLink accent={color} />
          </div>
          <ActColumn
            act={actLike as any}
            filters={NEUTRAL_FILTERS}
            onCrossRefClick={onCrossRefClick}
            hideSourceLink
            articleHideSources
            articleReaderHref={(num) => mandateHashFor(inst.id, num)}
            crossRefTarget={initialArt ? { actId: inst.actId, num: initialArt, ts: Date.now() } : null}
          />
        </section>
      )}
    </div>
  );
}
